import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { array, bool, func, number, object, string } from 'prop-types';
import classnames from 'classnames';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import FacetPanel from 'ps-components/FacetPanel/FacetPanel';
import FacetPanelLoading from 'ps-components/FacetPanel/FacetPanelLoading/FacetPanelLoading';
import Pagination from 'ps-components/Pagination/Pagination';
import Placeholder from '../Placeholder/Placeholder';
import Search from 'ps-components/Search/Search';
import SearchInfo from '../SearchInfo/SearchInfo';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import { getGenericRow, didSortChange } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import { SLIDEOUTS as MarketPaySlideouts } from '../../../components/Slideout/Slideouts';
import AdvancedSearch from '../AdvancedSearch/AdvancedSearch';
import FacetHeader from '../AdvancedSearch/FacetHeader/FacetHeader';
import { jobMatchGridActions } from '../LandingPage/JobMatchDetail/JobMatchGridCreate';

import { areAllFacetsUnchecked } from '../../../lib/facetHelpers';
import GG_DEFAULTS from '../../../lib/genericGridDefaults';
import './SurveyLibrary.scss';

SurveyLibrary.propTypes = {
  surveyLibraryFacet: object,
  surveyLibraryGrid: object,
  surveyLibraryGridActions: object,
  surveyLibraryFacetActions: object,
  location: object,
  currency: string,
  currentJob: object,
  displayIn: string,
  addMatch: func,
  deleteMatch: func,
  onMatchSelect: func,
  selectedSurveys: array,
  selectedBenchmarkYear: number,
  hideSelect: bool,
  selectedViewPk: number,
  onDataCutClick: func,
  loadingId: number,
  isMatchDeleting: bool,
  addSelectedCutID: func,
  removeSelectedCutID: func,
  showSlideout: func,
  hasCrowdSourcedMatch: bool,
  hideCrowdSourcedButton: bool,
  onSurveyJobTitleClick: func,
  togglePopover: func,
  renderPopover: bool,
  selectedRowId: number,
  updateModifiedDate: func,
  isAdHoc: bool,
  toggleHeader: func,
  showMinHeader: bool,
  history: object,
  getSurveyJobDetailsPageHref: func,
  setCurrency: func,
  refreshJobTabData: func,
  numericRounding: number,
  percentRounding: number,
  hideAddPrivateSurveyJobButton: bool,
  hideAddRegressionSurveyJobButton: bool,
  aging: number,
  currencies: array,
  hideFacet: bool,
  hideMatchOptions: bool,
};

function SurveyLibrary(props) {
  const dispatch = useDispatch();
  const facetId = 'survey-library__facet-panel';
  const advancedSearchFilterKey = facetId + '_advanced-search-filters';
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState({});
  const [addedMatches, setAddedMatches] = useState([]);
  const [facetCollapse, setFacetCollapse] = useState(true);
  const [useFacetSearch, setUseFacetSearch] = useState(!(localStorage.getItem(facetId + '_search_mode') === 'false'));
  const [sortState, setSortState] = useState(null);
  const firstUpdateFirst = useRef(true);
  const firstUpdateSecond = useRef(true);
  const firstUpdateThird = useRef(true);

  const {
    facetSelection,
    facets,
    loaded,
    loading,
    name: jobFacetName,
    query,
    showZeros,
    lastFacetFilteredID
  } = props.surveyLibraryFacet;

  const { hideFacet = false, hideMatchOptions = false } = props

  // Show 'Reset All' button in facet panel header if any options are unselected in facet panel
  const anyFacetsUnselected = areAllFacetsUnchecked(facets, facetSelection);
  const showResetAllButton = !anyFacetsUnselected || (query && query !== '');

  const { limit, offset } = props.surveyLibraryGrid.metadata;
  const totalCount = props.surveyLibraryGrid.metadata?.totalCount || 0;

  function searchCall(query) {
    setSearchText(query);
    props.surveyLibraryFacetActions.searchInputSearch(query);
  }

  function emptySearchCall() {
    searchCall('');
  }

  function search() {
    props.surveyLibraryFacetActions.searchInputSearch(query);
  }

  useEffect(() => {
    props.surveyLibraryGridActions.setSearchMode(useFacetSearch ? 'facet' : 'advanced');
  }, []);

  useEffect(() => {
    props.surveyLibraryFacetActions.setCurrency(props.currency);
  }, [props.currency]);

  useEffect(() => {
    if (props.selectedViewPk) {
      if (firstUpdateFirst.current) {
        firstUpdateFirst.current = false;
        if (props.location?.search) return;
      }
      if (useFacetSearch) {
        searchCall(query);
      }
    }
  }, [props.displayIn, props.currency, props.selectedViewPk]);

  useEffect(() => {
    if (firstUpdateThird.current) {
      firstUpdateThird.current = false;
      return;
    }
    if (!useFacetSearch) {
      searchCall(query);
    }
  }, [props.displayIn, props.currency]);

  useEffect(() => {
    if (firstUpdateSecond.current) {
      firstUpdateSecond.current = false;
      return;
    }
    setSearchText(query);
    if (!loading && useFacetSearch) {
      searchCall(query);
    }
  }, [query]);

  useEffect(() => {
    const selected = {};
    props.selectedSurveys?.forEach(survey => {
      selected[survey] = true;
      setSelected(selected);
    });
  }, [props.selectedSurveys]);

  useEffect(() => {
    if (props.location?.search !== '' || (showResetAllButton && useFacetSearch)) {
      // Gives time for query params to populate before calling endpoint
      setTimeout(() => {
        searchCall(query);
      }, 1000);
    }
  }, [Object.keys(props.surveyLibraryFacet.facetSelection).length]);

  // Logic for Reset All button
  function handleReset() {
    setSearchText('');
    props.surveyLibraryFacetActions.handleReset().then(() => {
      props.setCurrency(props.currency);
    });
  }

  // Pagination logic for org job list
  function pageOrgJobs(limit, offset) {
    props.surveyLibraryFacetActions.pageList(limit, offset);
  }

  // track text input change on every change
  function searchOnChange(e) {
    setSearchText(e?.target?.value);
  }

  // clicking x to clear search text
  function searchOnClick() {
    props.surveyLibraryFacetActions.setSearchQuery('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      props.surveyLibraryFacetActions.setSearchQuery(searchText);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch() {
    props.surveyLibraryFacetActions.setSearchQuery(searchText);
  }

  function useSelectAccessor(el) {
    return el.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value;
  }

  function isChecked(cutId) {
    return selected[cutId];
  }

  function handleSetAction(newSetValue, cutId, currency) {
    const {
      currentJob,
      addMatch,
      addSelectedCutID,
      removeSelectedCutID,
      deleteMatch,
      selectedBenchmarkYear,
      setCurrency,
      selectedSurveys
    } = props;
    dispatch(jobMatchGridActions.setIsStale(currentJob.id, true));
    if (newSetValue === true) {
      return addMatch(currentJob.id, cutId, null, false, selectedBenchmarkYear, null).then(() => {
        setCurrency(currency).then(() => addSelectedCutID(cutId));
      });
    } else {
      return deleteMatch(currentJob.id, [cutId], null, selectedBenchmarkYear, false, null).then(() => {
        selectedSurveys.length === 1
          ? setCurrency(currency).then(() => removeSelectedCutID(cutId))
          : removeSelectedCutID(cutId);
      });
    }
  }

  function isRowLoading(row, loadingId) {
    return row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value === loadingId;
  }

  function setSelectedCut(selectedMatch) {
    const { currentJob, selectedBenchmarkYear, onMatchSelect, surveyLibraryGrid, updateModifiedDate } = props;
    const cutId = selectedMatch;
    const newSetValue = !selected[cutId];
    const cutIdIndex = surveyLibraryGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CUT_ID);
    const currencyIdIndex = surveyLibraryGrid.terms.findIndex(term => term.term === GG_DEFAULTS.CURRENCY);
    const foundItem = surveyLibraryGrid.data.find(el => el[cutIdIndex] === selectedMatch);
    // TODO success and failure messages
    handleSetAction(newSetValue, cutId, foundItem[currencyIdIndex]).then(() => {
      updateModifiedDate(currentJob.id, selectedBenchmarkYear);
      selected[cutId] = newSetValue;
      const newAddedMatches = addedMatches;

      if (newSetValue === true) {
        newAddedMatches.push(cutId);
      } else {
        newAddedMatches.splice(newAddedMatches.indexOf(cutId), 1);
      }

      setAddedMatches(newAddedMatches);
      if (onMatchSelect) {
        onMatchSelect(foundItem[currencyIdIndex], newSetValue !== true);
      }
    });
  }

  // Checks for empty facet or org job results
  const showPlaceHolder =
    (props.surveyLibraryGrid?.data?.length === 0 ||
      (props.surveyLibraryGrid?.data && props.surveyLibraryGrid?.data[0].length === 0)) &&
    loaded &&
    !loading;

  let primaryPlaceholderText = 'Survey job matches will appear here';
  let secondaryPlaceholderText =
    'To see matches, use the search box to search by survey job info or start selecting filters on the left';

  if (query && query !== '') {
    primaryPlaceholderText = (
      <span>
        No search results for <b>“ {query} ”</b>
      </span>
    );
    secondaryPlaceholderText = 'Please try different keywords or adjust your filters';
  } else if (!anyFacetsUnselected) {
    primaryPlaceholderText = 'No results found for set filters';
    secondaryPlaceholderText =
      'Please ensure you have identified default datacuts for these surveys in your survey library';
  } else if (!useFacetSearch && props.surveyLibraryGrid.addedFilters?.length) {
    primaryPlaceholderText = 'No results found for set filters';
    secondaryPlaceholderText = 'Try adjusting the filters in the left panel';
  } else if (!useFacetSearch && !props.surveyLibraryGrid.addedFilters?.length) {
    primaryPlaceholderText = 'Survey job matches will appear here';
    secondaryPlaceholderText = (
      <span>
        To see matches, please set filters to the left and click <b>Search</b>
      </span>
    );
  }
  const noResultsPlaceholder = (
    <Placeholder
      containerClassName="survey-library__no-results-container"
      primaryText={primaryPlaceholderText}
      secondaryText={secondaryPlaceholderText}
    />
  );

  function showCrowdSourcedSlideout() {
    props.showSlideout(MarketPaySlideouts.CROWD_SOURCED_MARKET_PRICING, {
      benchmarkYear: props.selectedBenchmarkYear,
      orgJobCode: props.currentJob.code,
      orgJobCodeKey: props.currentJob.codeKey,
      orgJobCurrency: props.currentJob.currency,
      orgJobId: props.currentJob.id,
      orgJobTitle: props.currentJob.title,
      refreshJobTabData: props.refreshJobTabData
    });
  }

  function showPrivateSurveyJobMatchSlideout() {
    props.showSlideout(MarketPaySlideouts.PRIVATE_SURVEY_JOB_MATCH, {
      benchmarkYear: props.selectedBenchmarkYear,
      currencies: props.currencies,
      currency: props.currency,
      orgJobCode: props.currentJob.code,
      orgJobCurrency: props.currentJob.currency,
      orgJobCodeKey: props.currentJob.codeKey,
      orgJobId: props.currentJob.id
    });
  }

  function onScroll(e) {
    props.toggleHeader(e.target);
  }

  function toggleSearchView() {
    const newSearchMode = !useFacetSearch;
    localStorage.setItem(facetId + '_search_mode', newSearchMode);
    props.surveyLibraryGridActions.clearSort();
    props.surveyLibraryGridActions.setSearchMode(newSearchMode ? 'facet' : 'advanced');
    props.surveyLibraryGridActions.clearResultsOnly();
    props.history.push();
    props.surveyLibraryFacetActions.resetSurveyLibraryReducer().then(newSearchMode && emptySearchCall);
    props.surveyLibraryGridActions
      .clearAdvancedFilters()
      .then(!newSearchMode && props.surveyLibraryFacetActions.searchColumnsOnly);
    setUseFacetSearch(newSearchMode);
  }

  function getTextTerms(term) {
    return term.dataType === 'text';
  }

  function handleOnSortChange(sort) {
    const sortChanged = didSortChange(sort, sortState);
    setSortState(sort);
    if (sortChanged) props.surveyLibraryGridActions.onSortChange(sort).then(search);
  }
  const advancedClass = !useFacetSearch ? 'survey-library__advanced-search' : '';
  const headerRender = (
    <FacetHeader
      toggleSearchView={toggleSearchView}
      useFacetSearch={useFacetSearch}
      showResetAllButton={showResetAllButton}
      handleReset={handleReset}
    />
  );

  let filteredFacets = [...facets],
    filteredFacetSelection = Object.assign({}, facetSelection);
  if (props.currency && props.currency !== '%') {
    const currencyFacet = Object.assign(
      {},
      filteredFacets.find(facet => facet.field === 'currency.none')
    );
    filteredFacetSelection['currency'] = { [props.currency]: true };
    let currencyItems = currencyFacet.items?.filter(item => item.key === props.currency);
    currencyFacet.items = currencyItems?.map(item => Object.assign({ isDisabled: true }, item));
    filteredFacets[filteredFacets.findIndex(facet => facet.field === 'currency.none')] = currencyFacet;
  } else {
    filteredFacets = facets;
    filteredFacetSelection = facetSelection;
  }

  function getAddMatchOptions() {
    let addMatchOptions = [];
    if (!props.hideCrowdSourcedButton) {
      const crowdSourcedText = props.hasCrowdSourcedMatch ? 'Review Crowd-Sourced Match' : 'Add Crowd-Sourced Match';
      addMatchOptions.push(crowdSourcedText);
    }
    if (!props.hideAddPrivateSurveyJobButton) addMatchOptions.push('Add Private Survey Job Match');
    if (!props.hideAddRegressionSurveyJobButton) addMatchOptions.push('Add Regression Survey Job Match');

    return addMatchOptions;
  }

  function handleItemClick(option) {
    const openPrivateSurvey = option === 'Add Private Survey Job Match';
    const openCrowdSourced = option === 'Review Crowd-Sourced Match' || option === 'Add Crowd-Sourced Match';

    if (openPrivateSurvey) showPrivateSurveyJobMatchSlideout();
    else if (openCrowdSourced) showCrowdSourcedSlideout();
    else showRegressionSurveyJobMatchSlideout();
  }

  function showRegressionSurveyJobMatchSlideout() {
    props.showSlideout(MarketPaySlideouts.REGRESSION_SURVEY_JOB_MATCH, {
      aging: props.aging,
      benchmarkYear: props.selectedBenchmarkYear,
      currencies: props.currencies,
      currency: props.currency,
      orgJobCode: props.currentJob.code,
      orgJobCurrency: props.currentJob.currency,
      orgJobCodeKey: props.currentJob.codeKey,
      orgJobId: props.currentJob.id
    });
  }

  return (
    <div id="survey-library" className={classnames('survey-library', { 'advanced-search': !useFacetSearch })}>
      { !hideFacet && <>
        {props.surveyLibraryGrid.processInitialLoad && useFacetSearch ? (
          <FacetPanelLoading
            className={'landing-page__facet-panel org__job-facets'}
            numberOfFacets={5}
            numberOfSubItems={5}
            includeHeader={true}
          />
        ) : (
          <FacetPanel
            className="survey-library__facet-panel survey-library__facets"
            innerClassName={classnames('survey-library__facet-panel-inner', {
              min: props.showMinHeader,
              adhoc: props.isAdHoc
            })}
            canCollapse={true}
            facetItemClick={props.surveyLibraryFacetActions.facetItemClick}
            facetReset={props.surveyLibraryFacetActions.facetReset}
            facetSelection={filteredFacetSelection}
            facets={filteredFacets}
            id={facetId}
            isDisabled={false}
            isLoaded={loaded}
            limit={5}
            resetSearch={handleReset}
            showFacetSectionSearch
            showResetButton={showResetAllButton || false}
            showZeros={showZeros}
            isUncheckedInitially={true}
            collapseCallback={setFacetCollapse}
            title="Survey Library"
            collapseTooltip={facetCollapse ? 'Close survey library filters' : 'Open survey library filters'}
            renderChildren={!useFacetSearch}
            headerRender={headerRender}
            lastFacetFilteredID={lastFacetFilteredID}
            lockOtherFacets={props.surveyLibraryGrid.loading}
            scrollHeightCutoff={28}
          >
            <AdvancedSearch
              className={{
                min: props.showMinHeader,
                adhoc: props.isAdHoc
              }}
              selectedViewPk={props.selectedViewPk}
              availableFields={props.surveyLibraryGrid.terms?.filter(getTextTerms)}
              clearAdvancedFilters={props.surveyLibraryGridActions.clearAdvancedFilters}
              setAdvancedFilters={props.surveyLibraryGridActions.setAdvancedFilters}
              search={searchCall}
              searchColumnsOnly={props.surveyLibraryFacetActions.searchColumnsOnly}
              currentAdvancedFilters={props.surveyLibraryGrid.addedFilters}
              currency={props.currency}
              setFacetCurrency={props.surveyLibraryFacetActions.setCurrency}
              clearGrid={props.surveyLibraryGridActions.clearResultsOnly}
              defaultField="job_title"
              localFiltersKey={advancedSearchFilterKey}
              shouldInitialLoad={false}
            />
          </FacetPanel>
        )}
      </> }

      <div id="survey-library__content" className={classnames('survey-library__content', advancedClass)}>
      { !hideMatchOptions &&
        <div
          id="survey-library__search-container"
          className={classnames('survey-library__search-container', advancedClass, {
            'has-crowdsource': !props.hideCrowdSourcedButton
          })}
        >
          <div className="survey-library__searchbar-container">
            {useFacetSearch && (
              <React.Fragment>
                <Search
                  className={classnames('survey-library__search', { 'facet-offset': !facetCollapse })}
                  id="survey-library__search"
                  onChange={searchOnChange}
                  onClick={searchOnClick}
                  onKeyDown={searchOnKeyDown}
                  onSearch={searchOnSearch}
                  autoFocus={true}
                  placeholder="Search by job title, job code, survey title, or survey code"
                  testId="survey-library__search"
                  value={searchText}
                />
                <SearchInfo className="survey-library__search-info" />
              </React.Fragment>
            )}
          </div>
          <div className="survey-library__match-buttons">
            <Dropdown
              className="add-matches__dropdown"
              dropdownItems={getAddMatchOptions()}
              itemOnClick={handleItemClick}
              text="Other Match Options"
            />
          </div>
        </div> }

        {showPlaceHolder || (!showResetAllButton && useFacetSearch) ? (
          noResultsPlaceholder
        ) : (
          <React.Fragment>
            <div
              id="survey-library__list-container"
              className={classnames(
                'survey-library__list-container',
                {
                  min: props.showMinHeader,
                  adhoc: props.isAdHoc,
                  'has-crowdsource': !props.hideCrowdSourcedButton
                },
                advancedClass
              )}
              onScroll={onScroll}
            >
              <AdvancedTable
                {...props.surveyLibraryGrid.tableProps}
                {...props.surveyLibraryGridActions}
                getDataRow={(row, idx) =>
                  getGenericRow(row, idx, {
                    onClick: null,
                    isRowLoading: isRowLoading(row, props.loadingId),
                    isRowDeleting: props.isMatchDeleting,
                    loadingText: 'Adding Match to Pricing...',
                    deletingText: 'Removing Match from Pricing...',
                    addedMatches,
                    onDataCutClick: props.onDataCutClick,
                    onSurveyJobTitleClick: props.onSurveyJobTitleClick,
                    getSurveyJobDetailsPageHref: props.getSurveyJobDetailsPageHref,
                    togglePopover: props.togglePopover,
                    renderPopover: props.renderPopover,
                    selectedRowId: props.selectedRowId,
                    numericRounding: props.numericRounding,
                    percentRounding: props.percentRounding
                  })
                }
                useSelectAll={false}
                isAllChecked={false}
                stickyHeader={true}
                isChecked={isChecked}
                onCheck={setSelectedCut}
                useSelectAccessor={useSelectAccessor}
                noDataText={'No Surveys Found'}
                hideSelect={props.hideSelect}
                loadingText={'Loading Survey Job Data'}
                loadingId={props.loadingId}
                onSortChange={handleOnSortChange}
                allowResize={true}
                allowSorting={true}
                useClientSideSort={false}
              />
            </div>
            <div id="survey-library__pagination-container" className="survey-library__pagination-container">
              {!props.surveyLibraryGrid.tableProps.dataLoading && (
                <Pagination
                  className="survey-library__pagination"
                  offset={offset}
                  pageAction={pageOrgJobs}
                  pageShowRange={3}
                  scrollTopPageChange
                  shouldShowInput
                  step={limit}
                  totalCount={parseInt(totalCount)}
                />
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default SurveyLibrary;
